import React from 'react';
import { format, parseISO } from 'date-fns';
import { Risk } from '../../types/risk.types';
import {
  useArchiveRisk,
  useRestoreRisk,
  useRiskDetail,
  useRiskTasks,
  useUpdateRiskInline,
  useUserOptions,
} from '../../hooks/useRegisterQueries';
import { severityBgClass, severityLabel } from '../../lib/severity';
import { useToast } from '../../hooks/useToast';
import { LedgerSelect, LedgerSelectOption } from '../ui';
import ConfirmDialog from '../common/ConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/cn';

const LEVEL_LABELS = ['', 'V. LOW', 'LOW', 'MEDIUM', 'HIGH', 'V. HIGH'];
const LEVEL_OPTIONS: LedgerSelectOption[] = [1, 2, 3, 4, 5].map((n) => ({
  value: String(n),
  label: `${n} — ${LEVEL_LABELS[n]}`,
}));
// ARCHIVED is deliberately absent. Archiving is the Archive button's job and
// is gated to ADMIN/MANAGER; leaving it in this picker handed a USER-role
// owner the archive the button had just refused them, because the backend
// grants partial_update to an object's owner whatever their role. The
// serializer now rejects that transition too, so an option here would only
// offer an action the server refuses.
const STATUS_OPTIONS: LedgerSelectOption[] = ['DRAFT', 'ACTIVE', 'CLOSED'].map((s) => ({
  value: s,
  label: s.charAt(0) + s.slice(1).toLowerCase(),
}));

// Timestamps (created_at/updated_at) render in the viewer's local time, matching
// the dashboard Journal. Date-only fields (last_reviewed_date, task due dates)
// go through parseISO so 'YYYY-MM-DD' keeps local-midnight semantics and never
// slips a day for viewers west of Greenwich.
const stampLocal = (iso: string | null | undefined): string | null =>
  iso ? format(new Date(iso), 'MMM d, yyyy').toUpperCase() : null;

const stampDate = (dateStr: string | null | undefined, pattern: string): string | null =>
  dateStr ? format(parseISO(dateStr), pattern).toUpperCase() : null;

interface RegisterPeekProps {
  risk: Risk;
  onClose: () => void;
  onEdit: (id: string) => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="cap-label">{label}</div>
    <div className="mt-0.5 text-scale-1 text-ink">{children}</div>
  </div>
);

export const RegisterPeek: React.FC<RegisterPeekProps> = ({ risk, onClose, onEdit }) => {
  const detail = useRiskDetail(risk.id);
  const tasks = useRiskTasks(risk.id);
  const update = useUpdateRiskInline();
  const users = useUserOptions();
  const toast = useToast();

  const onSaveError = () => toast.error('Save failed');
  const ownerOptions: LedgerSelectOption[] = [
    { value: '', label: 'Unassigned' },
    ...(users.data ?? []).map((user) => ({ value: user.id, label: user.fullName })),
  ];
  const score = risk.residual_score ?? risk.residual_likelihood * risk.residual_impact;
  const inherentScore = risk.inherent_score ?? risk.inherent_likelihood * risk.inherent_impact;
  const taskRows = tasks.data ?? [];
  const archive = useArchiveRisk();
  const restore = useRestoreRisk();
  const [confirmingArchive, setConfirmingArchive] = React.useState(false);
  const isArchived = risk.status === 'ARCHIVED';
  // Archive needs ADMIN or MANAGER on the backend. Rendering it for everyone
  // would hand most users a button that 403s.
  const { isAdmin, isManager, isSuperuser } = usePermissions();
  const canArchive = isAdmin || isManager || isSuperuser;
  const updatedStamp = stampLocal(detail.data?.updated_at);

  return (
    <aside aria-label="Risk peek" className="border-l border-ink bg-paper-raised p-5">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-scale-0 text-ink-muted">
          {risk.reference_number} · {risk.status}
        </span>
        <span className="flex gap-3">
          <button type="button" onClick={() => onEdit(risk.id)} className="cap-label cursor-pointer !text-accent hover:underline">
            Edit ↗
          </button>
          {canArchive && !isArchived && (
            <button
              type="button"
              onClick={() => setConfirmingArchive(true)}
              disabled={archive.isPending}
              className="cap-label cursor-pointer text-ink-muted hover:!text-ink disabled:cursor-default disabled:opacity-50"
            >
              Archive
            </button>
          )}
          {/* The archive dialog tells the reader an administrator can restore
              this, so there has to be something that does. Before this, both
              restore service methods had zero callers and the backend hides
              archived rows from every list, which made archiving a one-way
              trip through the product whatever the copy promised. */}
          {canArchive && isArchived && (
            <button
              type="button"
              onClick={() => restore.mutate(risk.id, { onError: onSaveError })}
              disabled={restore.isPending}
              className="cap-label cursor-pointer !text-accent hover:underline disabled:cursor-default disabled:opacity-50"
            >
              {restore.isPending ? 'Restoring…' : 'Restore'}
            </button>
          )}
          <button type="button" aria-label="Close peek" onClick={onClose} className="cursor-pointer text-scale-0 text-ink-muted hover:text-ink">
            ✕
          </button>
        </span>
      </div>

      <h2 className="mt-1.5 font-serif text-scale-4 font-medium text-ink">{risk.title}</h2>
      <p className="mb-3 text-scale-1 text-ink-muted">{risk.description}</p>
      <div className="border-b border-ink" />

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 py-3">
        <Field label="Severity">
          <span className="inline-flex items-center">
            <span aria-hidden className={cn('mr-1.5 h-2 w-2', severityBgClass(score))} />
            {severityLabel(score)}
          </span>
        </Field>
        <Field label="Status">
          <LedgerSelect
            ariaLabel="Status"
            value={risk.status}
            options={STATUS_OPTIONS}
            disabled={update.isPending}
            onValueChange={(status) =>
              update.mutate({ id: risk.id, data: { status: status as Risk['status'] } }, { onError: onSaveError })
            }
          />
        </Field>
        <Field label="Owner">
          <LedgerSelect
            ariaLabel="Owner"
            value={risk.owner ?? ''}
            options={ownerOptions}
            disabled={update.isPending}
            onValueChange={(owner) => update.mutate({ id: risk.id, data: { owner } }, { onError: onSaveError })}
          />
        </Field>
        <Field label="Likelihood × Impact">
          <span className="font-mono">
            {LEVEL_LABELS[risk.inherent_likelihood]} × {LEVEL_LABELS[risk.inherent_impact]} → {inherentScore}
          </span>
        </Field>
        <Field label="Residual likelihood">
          <LedgerSelect
            ariaLabel="Residual likelihood"
            value={String(risk.residual_likelihood)}
            options={LEVEL_OPTIONS}
            disabled={update.isPending}
            onValueChange={(value) =>
              update.mutate({ id: risk.id, data: { residual_likelihood: Number(value) } }, { onError: onSaveError })
            }
          />
        </Field>
        <Field label="Residual impact">
          <LedgerSelect
            ariaLabel="Residual impact"
            value={String(risk.residual_impact)}
            options={LEVEL_OPTIONS}
            disabled={update.isPending}
            onValueChange={(value) =>
              update.mutate({ id: risk.id, data: { residual_impact: Number(value) } }, { onError: onSaveError })
            }
          />
        </Field>
        <Field label="Residual after treatment">
          <span className="font-mono">
            {score}
            {(risk.risk_reduction ?? 0) > 0 && (
              <span className="ml-1 text-scale-0 text-sev-minimal">−{Math.round(risk.risk_reduction ?? 0)}%</span>
            )}
          </span>
        </Field>
        <Field label="Category">{risk.category || '—'}</Field>
        {risk.vendor_note && <Field label="Vendor note">{risk.vendor_note}</Field>}
      </div>

      <div className="cap-label border-b border-ink pb-0.5">Treatment tasks · {taskRows.length}</div>
      {taskRows.length === 0 ? (
        <p className="py-2 font-serif text-scale-1 italic text-ink-muted">No treatment tasks on file.</p>
      ) : (
        taskRows.map((task) => {
          const done = task.status === 'COMPLETED';
          return (
            <div key={task.id} className="flex items-baseline gap-2 border-b border-rule-faint py-1.5 text-scale-1">
              <span aria-hidden className={cn('h-2.5 w-2.5 shrink-0 self-center border border-ink', done && 'bg-ink')} />
              <span className={cn('min-w-0 flex-1 truncate', done && 'text-ink-muted line-through')}>{task.title}</span>
              <span className="font-mono text-scale-0 text-ink-muted">
                {done ? 'DONE' : stampDate(task.dueDate, 'MMM d, yyyy') ?? '—'}
              </span>
            </div>
          );
        })
      )}

      <div className="cap-label mt-4 border-b border-ink pb-0.5">History</div>
      <div className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">
        <div>Recorded {stampLocal(detail.data?.created_at ?? risk.created_at)}</div>
        {updatedStamp && <div>Updated {updatedStamp}</div>}
        <div>
          {detail.data?.last_reviewed_date
            ? `Last reviewed ${stampDate(detail.data.last_reviewed_date, 'MMM d, yyyy')}`
            : 'Not yet reviewed'}
        </div>
      </div>
      <ConfirmDialog
        open={confirmingArchive}
        title="Archive this risk?"
        message={
          `${risk.reference_number} leaves the register. The record is kept and an ` +
          'administrator can restore it, so nothing is lost.'
        }
        confirmText="Archive"
        severity="warning"
        onCancel={() => setConfirmingArchive(false)}
        onConfirm={() => {
          setConfirmingArchive(false);
          archive.mutate(risk.id, {
            onSuccess: () => {
              toast.success(`${risk.reference_number} archived`);
              onClose();
            },
            onError: () => toast.error('Archive failed'),
          });
        }}
      />
    </aside>
  );
};
